/**
 * Default look of one pinned note.
 *
 * Two paths reach for this: the row's starter template, which lays out one note
 * per preset, and a note added by hand into a row with nothing left in it. A
 * note added into a row that still has notes copies its neighbour instead —
 * see PinnedNote/edit.jsx — so these presets are the shipped starting point,
 * not what every note ends up looking like.
 *
 * Keep the first two presets in sync with `example` in PinnedNotes/block.json;
 * block.json is static metadata and cannot import from here.
 */

import { __ } from '@wordpress/i18n';

/**
 * Tilt, pin colour and card colour, in insertion order.
 *
 * Each note leans the other way and pins a different colour, so a row reads as
 * pinned up by hand rather than as a stack of identical cards. Consumers cycle
 * the list, which is why appending a fifth note continues the pattern instead
 * of repeating the fourth.
 *
 * @type {Array<{rotate: number, pinColor: string, background: string}>}
 */
export const NOTE_PRESETS = [
  { rotate: -2, pinColor: '#5b8def', background: '#eef2ff' },
  { rotate: 1, pinColor: '#00d084', background: '#94f0cb' },
  { rotate: -1, pinColor: '#fcb900', background: '#ffe499' },
  { rotate: 2, pinColor: '#f78da7', background: '#ffbdcd' },
];

/** Radius of the note frame — the edge the shadow traces. */
const NOTE_RADIUS = {
  topLeft: '18px',
  topRight: '18px',
  bottomLeft: '18px',
  bottomRight: '18px',
};

/** Radius of the coloured card inside the frame, tucked in by its padding. */
const CARD_RADIUS = {
  topLeft: '14px',
  topRight: '14px',
  bottomLeft: '14px',
  bottomRight: '14px',
};

/**
 * Frame padding. Top is larger than the rest: that gap is what the pin hangs
 * in, and it is the only side the frame pads — see PinnedNote/style.scss.
 */
const NOTE_PADDING = {
  top: 'var:preset|spacing|50',
  bottom: 'var:preset|spacing|20',
  left: 'var:preset|spacing|20',
  right: 'var:preset|spacing|20',
};

/** Padding of the coloured card itself. */
const CARD_PADDING = {
  top: 'var:preset|spacing|20',
  bottom: 'var:preset|spacing|20',
  left: 'var:preset|spacing|20',
  right: 'var:preset|spacing|20',
};

/*
 * #00000033 rather than rgba(0, 0, 0, .2), which is the same colour: WP's style
 * engine drops a shadow value containing rgba() on the floor and emits no
 * box-shadow at all, with no warning anywhere.
 */
const NOTE_SHADOW = '6px 6px 9px #00000033';

/**
 * Attributes for the nth note, wrapping around the preset list.
 *
 * The frame styling is seeded as real attributes rather than left to CSS so an
 * author finds the radius, shadow and padding already filled in in the sidebar
 * and can change them from there — and so a note copying its neighbour picks
 * them up, which it could not do from a stylesheet. PinnedNote/style.scss
 * carries the same radius and shadow as a floor, for a note that arrives by
 * some path neither this nor the copy covers.
 *
 * @param {number} index Zero-based position in the row; values past the end wrap.
 * @return {Object} Block attributes.
 */
export function getNoteAttributes(index) {
  const { rotate, pinColor } = NOTE_PRESETS[index % NOTE_PRESETS.length];

  return {
    rotate,
    pinColor,
    style: {
      border: { radius: NOTE_RADIUS },
      shadow: NOTE_SHADOW,
      spacing: { padding: NOTE_PADDING },
      color: {
        background: 'white',
      }
    },
  };
}

/**
 * Inner blocks for the nth note: the coloured card and the sample question.
 *
 * The group is what paints the card; the note around it is only the frame. The
 * number is ordinary content, not an attribute — authors can make it "Q1", a
 * glyph, or delete it outright.
 *
 * @param {number} index Zero-based position in the row; values past the end wrap.
 * @return {Array} Inner-block template.
 */
export function getNoteInnerBlocks(index) {
  const { background } = NOTE_PRESETS[index % NOTE_PRESETS.length];

  return [
    [
      'core/group',
      {
        style: {
          color: { background },
          spacing: { padding: CARD_PADDING },
          border: { radius: CARD_RADIUS },
        },
        layout: { type: 'constrained' },
      },
      [
        [
          'core/paragraph',
          {
            content: String(index + 1).padStart(2, '0'),
            style: { typography: { fontSize: '14px' } },
          },
        ],
        [
          'core/heading',
          { level: 3, content: __('What is the delivery time?', 'brandy-blocks') },
        ],
        [
          'core/paragraph',
          {
            content: __(
              'Standard shipping typically takes 3-5 business days within the continental US, and 7-14 business days for international orders.',
              'brandy-blocks'
            ),
          },
        ],
      ],
    ],
  ];
}

/**
 * The row's starter template: one finished note per preset.
 *
 * Module-level constant on purpose: the block editor re-syncs inner blocks
 * whenever the template's identity changes, so this must not be rebuilt per
 * render.
 */
export const PINNED_NOTES_STARTER_NOTES = NOTE_PRESETS.map((_, index) => [
  'brandy/pinned-note',
  getNoteAttributes(index),
  getNoteInnerBlocks(index),
]);
